import { ComponentDataSourceService, DataSourceFunction } from '../../component-data-source.service';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

export const GROUPS_ENABLED_KEY = 'groups_enabled';

function isOrgSettingTrue(value: unknown): boolean {
  return value === true || value === 'true' || value === 1;
}

function resolveOrgIdFromUser(user: Record<string, unknown> | null | undefined): number | null {
  if (!user) return null;
  const orgId =
    user['organizations_id'] ??
    (user['user_info'] as Record<string, unknown> | undefined)?.['organizations_id'];
  if (orgId == null) return null;
  const n = typeof orgId === 'number' ? orgId : parseInt(String(orgId), 10);
  return Number.isFinite(n) ? n : null;
}

function fetchGroupsEnabled$(
  service: ComponentDataSourceService,
  orgId: number
): Observable<{ visible: boolean }> {
  const obs = service.executeDataSource('org_settings', { organizationId: orgId });
  if (!obs) {
    return of({ visible: false });
  }
  return obs.pipe(
    map((res: { data?: Record<string, unknown> }) => ({
      visible: isOrgSettingTrue(res?.data?.[GROUPS_ENABLED_KEY])
    })),
    catchError(() => of({ visible: false }))
  );
}

/**
 * Returns whether user groups (brugergrupperinger) are enabled for the logged-in user's organization.
 * Default is disabled (visible: false) when not logged in, org is unknown, or setting is missing.
 */
export function duna_groups_enabled(service: ComponentDataSourceService): DataSourceFunction {
  return {
    name: 'duna_groups_enabled',
    description:
      'Whether user groups (brugergrupperinger) are enabled for the logged-in user\'s organization. Returns { visible: boolean }.',
    execute: (_params?: LoadDataParams): Observable<{ visible: boolean }> => {
      const authService = service.authService;
      if (!authService.isAuthenticated) {
        return of({ visible: false });
      }

      const orgIdFromToken = resolveOrgIdFromUser(authService.getCurrentUser());
      if (orgIdFromToken != null) {
        return fetchGroupsEnabled$(service, orgIdFromToken);
      }

      return authService.getUserInfo().pipe(
        take(1),
        switchMap((info) => {
          const orgId = resolveOrgIdFromUser(info as Record<string, unknown>);
          if (orgId == null) {
            return of({ visible: false });
          }
          return fetchGroupsEnabled$(service, orgId);
        }),
        catchError(() => of({ visible: false }))
      );
    }
  };
}
